import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSEOMeta } from "@/components/SEO";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";

const quickLinks = [
  { label: "Home", href: "/", icon: "ri-home-line" },
  { label: "About Us", href: "/about", icon: "ri-information-line" },
  { label: "Our Work", href: "/our-work", icon: "ri-briefcase-line" },
  { label: "Contact", href: "/contact", icon: "ri-mail-line" },
  { label: "News", href: "/news", icon: "ri-newspaper-line" },
  { label: "Get Involved", href: "/get-involved", icon: "ri-hand-heart-line" },
];

export default function NotFound() {
  const navigate = useNavigate();

  useSEOMeta({
    title: "404 - Page Not Found | BHODEV Cameroon",
    description:
      "The page you are looking for does not exist. Explore BHODEV Cameroon's health, education, and development programs.",
    keywords:
      "BHODEV, 404, page not found, Cameroon NGO, community health",
    canonicalUrl: "https://bhodevcameroon.org/404",
    noindex: true,
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <div className="min-h-screen bg-stone-50 font-inter">
      <Navbar />

      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative min-h-[70vh] md:min-h-[75vh] flex items-center justify-center overflow-hidden">
          {/* Background Image */}
          <div className="absolute inset-0 z-0">
            <img
              src="https://readdy.ai/api/search-image?query=aerial%20view%20of%20Cameroon%20lush%20green%20rural%20landscape%20with%20misty%20hills%20and%20villages%20warm%20golden%20hour%20lighting%20documentary%20photography%20vibrant%20natural%20colors&width=1600&height=900&seq=404-bg&orientation=landscape"
              alt="Cameroon landscape background"
              title="Beautiful Cameroon landscape"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-stone-900/60"></div>
          </div>

          {/* Content */}
          <div className="relative z-10 text-center px-4 max-w-2xl mx-auto animate-fade-in-up">
            {/* 404 Display */}
            <div className="relative mb-6">
              <span className="text-[8rem] md:text-[10rem] font-black text-white/10 select-none leading-none">
                404
              </span>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-5xl md:text-6xl font-black text-white font-saria">
                  404
                </span>
              </div>
            </div>

            <h1 className="text-2xl md:text-3xl font-bold text-white mb-4 font-saria">
              Page Not Found
            </h1>
            <p className="text-base md:text-lg text-white/70 mb-8 max-w-md mx-auto leading-relaxed">
              The page you are looking for may have been moved, renamed, or is temporarily unavailable. Let us help you find your way.
            </p>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 px-6 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-white text-sm font-semibold hover:bg-white/20 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                Go Back
              </button>
              <Link
                to="/"
                className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-full text-sm font-semibold hover:bg-primary-dark transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg shadow-primary/30"
              >
                <i className="ri-home-line"></i>
                Back to Home
              </Link>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-4 mb-8">
              <div className="flex-1 h-px bg-white/20"></div>
              <span className="text-xs text-white/50 uppercase tracking-widest font-medium">
                Or explore
              </span>
              <div className="flex-1 h-px bg-white/20"></div>
            </div>
          </div>
        </section>

        {/* Quick Links Grid */}
        <section className="bg-white py-16 md:py-20 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-xl md:text-2xl font-bold text-stone-800 mb-2 font-saria">
                Popular Pages
              </h2>
              <p className="text-sm text-stone-500">
                Navigate to one of these sections on our website
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5">
              {quickLinks.map((link, index) => (
                <Link
                  key={link.label}
                  to={link.href}
                  className="group flex flex-col items-center gap-3 p-5 md:p-6 bg-stone-50 rounded-2xl border border-stone-100 hover:border-primary/30 hover:bg-secondary/30 transition-all duration-300 cursor-pointer animate-fade-in-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-primary/10 rounded-xl group-hover:bg-primary group-hover:text-white transition-all duration-300">
                    <i className={`${link.icon} text-xl text-primary group-hover:text-white`}></i>
                  </div>
                  <span className="text-sm font-semibold text-stone-700 group-hover:text-primary transition-colors duration-300">
                    {link.label}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Help CTA */}
        <section className="bg-stone-900 py-14 md:py-16 px-4">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-14 h-14 flex items-center justify-center bg-primary/20 rounded-2xl mx-auto mb-5">
              <i className="ri-questionnaire-line text-2xl text-primary"></i>
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-white mb-3 font-saria">
              Still Lost?
            </h2>
            <p className="text-sm text-stone-400 mb-6 leading-relaxed max-w-md mx-auto">
              If you believe this page should exist or need assistance finding what you are looking for, reach out to our team.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/contact"
                className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-full text-sm font-semibold hover:bg-primary-dark transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg shadow-primary/25"
              >
                <i className="ri-mail-line"></i>
                Contact Us
              </Link>
              <a
                href="mailto:info@bhodevcameroon.org"
                className="flex items-center gap-2 px-6 py-3 bg-stone-800 text-white rounded-full text-sm font-semibold hover:bg-stone-700 transition-all duration-300 cursor-pointer whitespace-nowrap border border-stone-700"
              >
                <i className="ri-send-plane-line"></i>
                Email Support
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}